import { useDispatch, useSelector } from 'react-redux';
import Modal from 'components/Modal/Modal';
import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { setDialogState } from 'reduxStore/reducers/dialogSlice';
import LoginChildren from './component/Login';
import {
  emailRegExp,
  email_error,
  login,
  login_text1,
  login_text4,
  password_error,
  first_name_error,
  last_name_error,
  signup,
} from './constants/text';
import { loginUser, registerUser } from 'reduxStore/actions/auth';
import { getProfileDetails } from 'reduxStore/actions/user';

const LOGIN_RETRY_TOAST_ID = 'login-retry-toast';

const Login = () => {
  const { dialogOpenState } = useSelector((state) => state.dialogOpenState);
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [passwordHide, setPasswordHide] = useState(true);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [retryCountdown, setRetryCountdown] = useState(0);


  const isLogin = !dialogOpenState.register;

  const dismissRetryToast = () => {
    toast.dismiss(LOGIN_RETRY_TOAST_ID);
  };

  const loginValidationSchema = Yup.object({
    email: Yup.string()
      .transform((value) => (value.trim() === '' ? '' : value.trim()))
      .required(email_error.require_email)
      .min(10, 'Email must be at least 10 characters long')
      .max(30, 'Email must be less than 30 characters long')
      .matches(emailRegExp, email_error.valid_email)
      .matches(
        /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
        'Email cannot contain capital letters'
      ).matches(
      /^[\w.-]+@[^0-9][\w.-]+\.[a-zA-Z]{2,}$/,
      "Email cannot have a number after @"
    ),

    password: Yup.string()
      .required(password_error)
      .trim()
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(
        /[@$!%*?&#]/,
        'Password must contain at least one special character (@, $, !, %, *, ?, &)'
      )
      .min(8, 'Password must be at least 8 characters long')
      .max(15, 'Password must be less than 15 characters long')

  });
  
  const signupValidationSchema = Yup.object({
    email: Yup.string()
      .transform((value) => (value.trim() === '' ? '' : value.trim()))
      .required(email_error.require_email)
      .min(10, 'Email must be at least 10 characters long')
      .max(30, 'Email must be less than 30 characters long')
      .matches(emailRegExp, email_error.valid_email)
      .matches(
        /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
        'Email cannot contain capital letters'
      ).matches(
      /^[\w.-]+@[^0-9][\w.-]+\.[a-zA-Z]{2,}$/,
      "Email cannot have a number after @"
    )
,
    password: Yup.string()
      .required(password_error)
      .trim()
      .min(8, 'Password must be at least 8 characters long')
      .max(15, 'Password must be less than 15 characters long')

      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(
        /[@$!%*?&#]/,
        'Password must contain at least one special character (@, $, !, %, *, ?, &)'
      ),

    first_name: Yup.string()
      .required(first_name_error)

      .min(2, 'First name must be at least 2 characters')
      .max(20, 'First name cannot be more than 20 characters')
      .matches(
        /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
        'Name can only contain alphabet letters.'
      ),

    last_name: Yup.string()
      .required(last_name_error)

      .min(2, 'Last name must be at least 2 characters')
      .max(20, 'First name cannot be more than 20 characters')
      .matches(
        /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
        'Name can only contain alphabet letters.'
      ),

    country: Yup.string().required('Country is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      country: '',
    },
    validationSchema: isLogin ? loginValidationSchema : signupValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmitAPICall();
    },
  });

  formikRef.current = formik;

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    setPasswordHide(true);
    setRetryCountdown(0);
    dismissRetryToast();
  }, [isLogin]);

  useEffect(() => {
    if (retryCountdown <= 0) return undefined;

    const interval = setInterval(() => {
      setRetryCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [retryCountdown > 0]);

  const formatRetryTime = (totalSeconds) => {
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const startRetryTimer = (retryAfter) => {
    const retryAfterMinutes = Number(retryAfter);
    if (!retryAfterMinutes || Number.isNaN(retryAfterMinutes)) return;

    dismissRetryToast();
    setRetryCountdown(Math.ceil(retryAfterMinutes * 60));
  };

  useEffect(() => () => dismissRetryToast(), []);

  

  const onSubmitAPICall = async () => {
    if (isLogin) {
      if (retryCountdown > 0) return;

      try {
        const loginResponse = await dispatch(
          loginUser({
            email: formik.values.email,
            password: formik.values.password,
          })
        ).unwrap();

        toast.success(loginResponse?.message || 'Login successful');
        dismissRetryToast();
        setRetryCountdown(0);

        await dispatch(getProfileDetails()).unwrap();

        dispatch(
          setDialogState({
            ...dialogOpenState,
            logIn: false,
            register: false,
          })
        );
        // window.location.reload();
      } catch (error) {
        if (error?.requires_verification) {
          localStorage.setItem('signupEmail', formik.values.email);

          dispatch(
            setDialogState({
              ...dialogOpenState,
              logIn: false,
              isLoginOtp: true,
              loginOtpEmail: error.email,
            })
          );
          return;
        }
        if(error?.message === "User with this email does not exist."){
          toast.error(error?.message);
          formik.resetForm();
          setPasswordHide(true);
          setDropdownKey((prev) => prev + 1);
          return;
        }

        if (error?.retryAfter) {
          startRetryTimer(error.retryAfter);
          // console.log('error:', error);
          // toast.error(
          //   `${error.message}`
          // );
          return;
        }

        toast.error(error?.message || 'Something went wrong');
      }
    } else {
      //register user flow
      dispatch(
        registerUser({
          email: formik.values.email,
          password: formik.values.password,
          first_name: formik.values.first_name,
          last_name: formik.values.last_name,
          country: formik.values.country,
        })
      )
        .unwrap()
        .then((registerResponse) => {
      
          if (registerResponse?.success) {
            toast.success(registerResponse?.message || 'User registered successfully');
            localStorage.setItem('signupEmail', formik.values.email);
            dispatch(
              setDialogState({
                logIn: false,
                register: false,
                otp: true,
                isSignupOtp: true,
              })
            );
             
          }
        })
        .catch((error) => {
        
          const message =
            typeof error === 'string' ? error : error?.message;
        
          if (message === 'User with this email already exists!') {
            formik.resetForm();
            setPasswordHide(true);
            setDropdownKey((prev) => prev + 1);
            return;
          }
         
          if(message === "User with this email does not exist."){
            formik.resetForm();
            setPasswordHide(true);
            setDropdownKey((prev) => prev + 1);
            return;
          }
        
          toast.error(error?.message || 'Something went wrong');
        });
    }
  };
  

  return (
    <Modal
      modalContentClassname={`sm:min-w-[490px] ${dialogOpenState.login ? 'max-h-[90vh] overflow-y-auto sidebar-scrollbar' : ''}`}
      title={dialogOpenState.register ? signup : login}
      disableOutsideClick={true}
      closeHandler={() => {
        dismissRetryToast();
        setRetryCountdown(0);
        dispatch(
          setDialogState({
            ...dialogOpenState,
            logIn: false,
            register: false,
          })
        );
      }}
      text={dialogOpenState.register ? login_text1 : login_text4}
      //on reset form also reset dropdown field
      children={
        <LoginChildren
          passwordHide={passwordHide}
          setPasswordHide={setPasswordHide}
          isLogin={!dialogOpenState.register}
          formik={formik}
          dropdownKey={dropdownKey}
          retryCountdown={retryCountdown}
          formatRetryTime={formatRetryTime}
        />
      }
    />
  );
};

export default Login;
